/**
 * A6 inventory field policy (frontend mirror of the backend policy in
 * `ise-srp-back/src/srp/srpLifecycleFields.ts`).
 *
 * Two distinct groups of fields are kept off the SRP/custom-feed field patch:
 *
 * 1. A6_OPERATIONAL_INVENTORY_FIELDS — dealer/feed inventory classification attributes
 *    (inventoryState, inventoryUsageType, website visibility). These ARE dealer-authored: they stay
 *    visible and EDITABLE in the Add/Edit form, but they must be persisted through the operational
 *    metadata / draft path (operationalPatch), NOT through the normal SRP/custom-feed field patch.
 *    Their source of truth is CurrentInventory / ArchivedInventory / VehicleDraft.operationalPatch.
 *    `inventoryState` is projected to the website field `state` by the backend.
 *
 * 2. A6_SYSTEM_WORKFLOW_FIELDS — system/workflow fields the dealer does not author. They are
 *    hidden/read-only in the UI and must never appear in any save payload.
 */

export const A6_OPERATIONAL_INVENTORY_FIELDS: readonly string[] = [
  'inventoryState',
  'inventoryStateMode',
  'inventoryUsageType',
  'inventoryUsageTypeMode',
  'websiteVisibility',
  'websiteVisibilityMode',
  'websiteVisibilityReason',
] as const;

export const A6_SYSTEM_WORKFLOW_FIELDS: readonly string[] = [
  'publishState',
  'archiveReason',
  'membershipStatus',
  'websiteVisibilityChangedAt',
  'websiteVisibilityChangedBy',
] as const;

const A6_OPERATIONAL_INVENTORY_FIELD_SET = new Set<string>(A6_OPERATIONAL_INVENTORY_FIELDS);
const A6_SYSTEM_WORKFLOW_FIELD_SET = new Set<string>(A6_SYSTEM_WORKFLOW_FIELDS);

/** Dealer/feed operational inventory attribute (editable, persisted via operationalPatch). */
export function isA6OperationalInventoryField(field: string): boolean {
  return A6_OPERATIONAL_INVENTORY_FIELD_SET.has(field);
}

/** System/workflow field the dealer does not author (hidden/read-only, never sent). */
export function isA6SystemWorkflowField(field: string): boolean {
  return A6_SYSTEM_WORKFLOW_FIELD_SET.has(field);
}

/**
 * Returns true if a field must be excluded from the SRP / custom-feed field patch.
 * Both operational inventory attributes and system/workflow fields are excluded — operational
 * attributes travel through the operationalPatch path instead.
 */
export function isExcludedFromSrpPatch(field: string): boolean {
  return isA6OperationalInventoryField(field) || isA6SystemWorkflowField(field);
}

/**
 * @deprecated Prefer the explicit `A6_OPERATIONAL_INVENTORY_FIELDS` / `A6_SYSTEM_WORKFLOW_FIELDS`
 * split. Retained as a stable alias of the full excluded set.
 */
export const A6_LIFECYCLE_FIELDS: readonly string[] = [
  ...A6_OPERATIONAL_INVENTORY_FIELDS,
  ...A6_SYSTEM_WORKFLOW_FIELDS,
];

/**
 * @deprecated Prefer `isExcludedFromSrpPatch`. Returns true if the field must stay out of SRP payloads.
 */
export function isA6LifecycleField(field: string): boolean {
  return isExcludedFromSrpPatch(field);
}
